import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  Select,
  useToast,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Switch,
  Alert,
  AlertIcon,
  Code,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiMail,
  FiMessageSquare,
  FiCopy,
  FiMoreVertical,
  FiEye,
} from 'react-icons/fi';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import RichTextEmailEditor from '../Email/RichTextEmailEditor';
import DynamicVariableInsertButton from '../Email/DynamicVariableInsertButton';
import EmailPreviewModal from '../Email/EmailPreviewModal';
import SmsMediaPicker from '../Dialpad/SmsMediaPicker';
import { useAuth } from '../../../contexts/AuthContext';
import { insertTagIntoTextarea } from '../../../utils/templateVariables';

// Default templates to seed on first load
const DEFAULT_EMAIL_TEMPLATES = [
  {
    name: 'Welcome Email',
    subject: 'Welcome! Let\'s Get Started on Your Mortgage',
    body: `Hi {{firstName}},

Thank you for choosing us for your mortgage needs! I'm excited to help you find the perfect loan solution.

I wanted to reach out personally to introduce myself and let you know that I'm here to guide you through every step of the process.

Next Steps:
- Review your financial documents
- Discuss loan options that fit your needs
- Answer any questions you may have

When would be a good time for a quick call this week?

Best regards,
{{loanOfficerName}}`,
    category: 'welcome',
    isActive: true,
  },
  {
    name: 'Follow-Up',
    subject: 'Following Up on Your Mortgage Application',
    body: `Hi {{firstName}},

I wanted to follow up regarding your {{product}} loan application. I hope you're doing well!

Have you had a chance to review the information I sent over? I'm here to answer any questions you might have.

Please let me know if you'd like to schedule a time to chat, or if there's anything else I can help clarify.

Looking forward to hearing from you!

Best,
{{loanOfficerName}}`,
    category: 'followup',
    isActive: true,
  },
  {
    name: 'Quote Request',
    subject: 'Your Personalized Mortgage Quote',
    body: `Hi {{firstName}},

Thank you for your interest in a {{product}} loan!

I've prepared a personalized quote based on the information you provided. To give you the most accurate rates and terms, I'd love to discuss your specific situation.

Could we schedule a 15-minute call this week? I'll walk you through:
- Current rates and terms
- Monthly payment estimates
- Available loan programs
- Next steps in the process

What day works best for you?

Best regards,
{{loanOfficerName}}`,
    category: 'quote',
    isActive: true,
  },
];

const DEFAULT_SMS_TEMPLATES = [
  {
    name: 'Introduction',
    body: `Hi {{firstName}}! This is {{loanOfficerName}} from Future Home Loans. I received your mortgage inquiry and I'd love to help you find the best loan option. When's a good time to chat? 📞`,
    category: 'intro',
    isActive: true,
  },
  {
    name: 'Follow-Up',
    body: `Hi {{firstName}}, just checking in on your {{product}} loan application. Do you have any questions I can help answer? Feel free to call me anytime!`,
    category: 'followup',
    isActive: true,
  },
  {
    name: 'Documents Needed',
    body: `Hi {{firstName}}, we're almost there! Just need a few more documents to complete your application. I'll send you an email with the list. Let me know if you have any questions!`,
    category: 'documents',
    isActive: true,
  },
];

export default function TemplateManager() {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateType, setTemplateType] = useState('email');
  const [loading, setLoading] = useState(true);
  const [seededDefaults, setSeededDefaults] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBG = useColorModeValue('gray.50', 'gray.800');
  const hoverBG = useColorModeValue('gray.50', 'gray.700');

  // Load email templates
  useEffect(() => {
    const templatesRef = collection(db, 'emailTemplates');
    const q = query(templatesRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const templates = [];
      snapshot.forEach((doc) => {
        templates.push({ id: doc.id, ...doc.data() });
      });
      setEmailTemplates(templates);

      // Seed defaults if collection is empty
      if (templates.length === 0 && !seededDefaults) {
        seedDefaultTemplates('email');
        setSeededDefaults(true);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [seededDefaults]);

  // Load SMS templates
  useEffect(() => {
    const templatesRef = collection(db, 'smsTemplates');
    const q = query(templatesRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const templates = [];
      snapshot.forEach((doc) => {
        templates.push({ id: doc.id, ...doc.data() });
      });
      setSmsTemplates(templates);

      // Seed defaults if collection is empty
      if (templates.length === 0 && !seededDefaults) {
        seedDefaultTemplates('sms');
      }
    });

    return () => unsubscribe();
  }, [seededDefaults]);

  const seedDefaultTemplates = async (type) => {
    try {
      const templates = type === 'email' ? DEFAULT_EMAIL_TEMPLATES : DEFAULT_SMS_TEMPLATES;
      const collectionName = type === 'email' ? 'emailTemplates' : 'smsTemplates';

      const promises = templates.map((template) =>
        addDoc(collection(db, collectionName), {
          ...template,
          createdBy: 'system',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })
      );

      await Promise.all(promises);

      toast({
        title: 'Default templates created',
        description: `Added ${templates.length} default ${type} templates`,
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error seeding templates:', error);
    }
  };

  const handleAddTemplate = () => {
    setSelectedTemplate(null);
    onOpen();
  };

  const handleEditTemplate = (template, type) => {
    setSelectedTemplate({ ...template, type });
    onOpen();
  };

  const handleDeleteTemplate = async (templateId, type) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      const collectionName = type === 'email' ? 'emailTemplates' : 'smsTemplates';
      await deleteDoc(doc(db, collectionName, templateId));

      toast({
        title: 'Template deleted',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      console.error('Error deleting template:', error);
      toast({
        title: 'Error deleting template',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleToggleActive = async (templateId, type, currentStatus) => {
    try {
      const collectionName = type === 'email' ? 'emailTemplates' : 'smsTemplates';
      const templateRef = doc(db, collectionName, templateId);
      
      await updateDoc(templateRef, {
        isActive: !currentStatus,
        updatedAt: serverTimestamp(),
      });

      toast({
        title: currentStatus ? 'Template deactivated' : 'Template activated',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      console.error('Error toggling template:', error);
      toast({
        title: 'Error updating template',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    }
  };

  const renderTemplateTable = (templates, type) => (
    <Box overflowX="auto">
      <Table variant="simple" size="sm">
        <Thead bg={tableHeaderBG}>
          <Tr>
            <Th>Name</Th>
            <Th>Category</Th>
            {type === 'email' && <Th>Subject</Th>}
            <Th>Preview</Th>
            <Th>Status</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {templates.map((template) => (
            <Tr key={template.id} _hover={{ bg: hoverBG }}>
              <Td fontWeight="medium">{template.name}</Td>
              <Td>
                <Badge size="sm" colorScheme="purple">
                  {template.category}
                </Badge>
              </Td>
              {type === 'email' && (
                <Td fontSize="sm" noOfLines={1} maxW="200px">
                  {template.subject}
                </Td>
              )}
              <Td fontSize="xs" color="gray.600" noOfLines={2} maxW="300px">
                {template.body.substring(0, 100)}...
              </Td>
              <Td>
                <Switch
                  isChecked={template.isActive}
                  onChange={() => handleToggleActive(template.id, type, template.isActive)}
                  colorScheme="green"
                  size="sm"
                />
              </Td>
              <Td>
                <HStack spacing={1}>
                  <IconButton
                    icon={<FiEdit2 />}
                    size="xs"
                    variant="ghost"
                    aria-label="Edit template"
                    onClick={() => handleEditTemplate(template, type)}
                  />
                  <IconButton
                    icon={<FiTrash2 />}
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    aria-label="Delete template"
                    onClick={() => handleDeleteTemplate(template.id, type)}
                  />
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      {templates.length === 0 && (
        <Box textAlign="center" py={8}>
          <Text color="gray.500">No templates yet</Text>
        </Box>
      )}
    </Box>
  );

  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between">
          <VStack align="flex-start" spacing={0}>
            <Heading size="md">Communication Templates</Heading>
            <Text fontSize="sm" color="gray.600">
              Manage email and SMS templates for your team
            </Text>
          </VStack>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={handleAddTemplate}
          >
            Add Template
          </Button>
        </HStack>

        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <VStack align="flex-start" spacing={1} flex={1}>
            <Text fontSize="sm" fontWeight="semibold">
              Available Variables
            </Text>
            <Text fontSize="xs">
              Every contact, deal, and sender field works in both email and SMS templates —
              use the insert-variable button in the editor to browse them all. Common examples:
            </Text>
            <Text fontSize="xs">
              <Text as="span" fontWeight="medium" color="blue.600">Contact:</Text>{' '}
              <Code fontSize="xs">{'{{firstName}}'}</Code>{' '}
              <Code fontSize="xs">{'{{lastName}}'}</Code>{' '}
              <Code fontSize="xs">{'{{email}}'}</Code>{' '}
              <Code fontSize="xs">{'{{phone}}'}</Code>{' '}
              <Code fontSize="xs">{'{{company}}'}</Code>
            </Text>
            <Text fontSize="xs">
              <Text as="span" fontWeight="medium" color="green.600">Deal:</Text>{' '}
              <Code fontSize="xs">{'{{product}}'}</Code>{' '}
              <Code fontSize="xs">{'{{amount}}'}</Code>{' '}
              <Code fontSize="xs">{'{{dealName}}'}</Code>{' '}
              <Code fontSize="xs">{'{{loanNumber}}'}</Code>{' '}
              <Code fontSize="xs">{'{{lender}}'}</Code>{' '}
              <Code fontSize="xs">{'{{loanPurpose}}'}</Code>{' '}
              <Code fontSize="xs">{'{{propertyAddress}}'}</Code>{' '}
              <Code fontSize="xs">{'{{estimatedClosingDate}}'}</Code>
            </Text>
            <Text fontSize="xs">
              <Text as="span" fontWeight="medium" color="purple.600">Sender:</Text>{' '}
              <Code fontSize="xs">{'{{loanOfficerName}}'}</Code>{' '}
              <Code fontSize="xs">{'{{senderName}}'}</Code>{' '}
              <Code fontSize="xs">{'{{senderEmail}}'}</Code>{' '}
              <Code fontSize="xs">{'{{senderPhone}}'}</Code>
            </Text>
            <Text fontSize="xs" color="gray.600">
              Dot-notation works too, e.g. <Code fontSize="xs">{'{{contact.jobTitle}}'}</Code> or{' '}
              <Code fontSize="xs">{'{{deal.interestRate}}'}</Code>.
            </Text>
          </VStack>
        </Alert>

        <Tabs colorScheme="blue">
          <TabList>
            <Tab onClick={() => setTemplateType('email')}>
              <HStack spacing={2}>
                <FiMail />
                <Text>Email Templates</Text>
                <Badge>{emailTemplates.length}</Badge>
              </HStack>
            </Tab>
            <Tab onClick={() => setTemplateType('sms')}>
              <HStack spacing={2}>
                <FiMessageSquare />
                <Text>SMS Templates</Text>
                <Badge>{smsTemplates.length}</Badge>
              </HStack>
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0} pt={4}>
              {renderTemplateTable(emailTemplates, 'email')}
            </TabPanel>
            <TabPanel px={0} pt={4}>
              {renderTemplateTable(smsTemplates, 'sms')}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>

      {/* Template Editor Modal */}
      <TemplateEditorModal
        isOpen={isOpen}
        onClose={onClose}
        template={selectedTemplate}
        templateType={templateType}
      />
    </Box>
  );
}

/**
 * Categories are matched EXACTLY by the My Marketing pickers
 * (where('category', '==', 'newsletter')), so normalize what admins type:
 * lowercase + trimmed, and common variants of "newsletter" (Newsletter,
 * newsletters, news letter…) collapse to the canonical 'newsletter'.
 */
function normalizeCategory(raw) {
  const category = (raw || '').trim().toLowerCase();
  if (/^news\s*letters?$/.test(category)) return 'newsletter';
  return category;
}

function TemplateEditorModal({ isOpen, onClose, template, templateType }) {
  const editorRef = React.useRef(null);
  const smsBodyRef = React.useRef(null);
  const { currentUser } = useAuth();
  const toast = useToast();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [media, setMedia] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (template) {
      setName(template.name || '');
      setCategory(template.category || '');
      setSubject(template.subject || '');
      setBody(template.body || '');
      setMedia(template.media || null);
      setIsActive(template.isActive !== undefined ? template.isActive : true);
    } else {
      setName('');
      setCategory('');
      setSubject('');
      setBody('');
      setMedia(null);
      setIsActive(true);
    }
  }, [template, isOpen]);

  const handleSave = async () => {
    const smsImageOnly = templateType === 'sms' && !!media;
    if (!name.trim() || (!body.trim() && !smsImageOnly)) {
      toast({
        title: 'Missing required fields',
        description: templateType === 'sms' ? 'Name and a message or image are required' : 'Name and body are required',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (templateType === 'email' && !subject.trim()) {
      toast({
        title: 'Missing subject',
        description: 'Email templates require a subject line',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsSaving(true);
    try {
      const collectionName = templateType === 'email' ? 'emailTemplates' : 'smsTemplates';
      const templateData = {
        name: name.trim(),
        category: normalizeCategory(category) || 'general',
        body: body.trim(),
        isActive,
        updatedAt: serverTimestamp(),
      };

      if (templateType === 'email') {
        templateData.subject = subject.trim();
      }

      if (templateType === 'sms') {
        templateData.media = media || null;
      }

      // Confirm gallery visibility so admins publishing newsletters know
      // exactly what loan officers will (or won't) see in My Marketing.
      const isNewsletter = templateType === 'email' && templateData.category === 'newsletter';
      const newsletterNote = !isNewsletter
        ? undefined
        : isActive
          ? "Live in every loan officer's My Marketing → Templates gallery."
          : 'Saved as inactive — hidden from the My Marketing gallery until you activate it.';

      if (template && template.id) {
        // Update existing
        const templateRef = doc(db, collectionName, template.id);
        await updateDoc(templateRef, templateData);

        toast({
          title: 'Template updated',
          description: newsletterNote,
          status: 'success',
          duration: isNewsletter ? 5000 : 2000,
        });
      } else {
        // Create new
        templateData.createdBy = currentUser.uid;
        templateData.createdAt = serverTimestamp();

        await addDoc(collection(db, collectionName), templateData);

        toast({
          title: 'Template created',
          description: newsletterNote,
          status: 'success',
          duration: isNewsletter ? 5000 : 2000,
        });
      }

      onClose();
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: 'Error saving template',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>
          {template ? 'Edit Template' : 'Create Template'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm">Template Name</FormLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Welcome Email"
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Category</FormLabel>
              <Input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g., welcome, followup, quote, newsletter"
                list="template-category-suggestions"
              />
              <datalist id="template-category-suggestions">
                <option value="welcome" />
                <option value="followup" />
                <option value="quote" />
                <option value="intro" />
                <option value="documents" />
                <option value="newsletter" />
                <option value="general" />
              </datalist>
              <FormHelperText fontSize="xs">
                Templates in the <b>newsletter</b> category appear in every loan officer&apos;s
                My Marketing → Templates gallery (only while <b>Active</b> — the toggle below
                hides them everywhere and pauses synced newsletter sends).
              </FormHelperText>
            </FormControl>

            {templateType === 'email' && (
              <FormControl isRequired>
                <FormLabel fontSize="sm">Subject Line</FormLabel>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Email subject..."
                />
              </FormControl>
            )}

            <FormControl isRequired>
              <HStack justify="space-between" align="center" mb={1}>
                <FormLabel fontSize="sm" mb={0}>Message Body</FormLabel>
                {templateType === 'sms' && (
                  <DynamicVariableInsertButton
                    onInsert={(tag) => insertTagIntoTextarea(smsBodyRef, body, tag, setBody)}
                  />
                )}
              </HStack>
              {templateType === 'email' ? (
                <>
                  <RichTextEmailEditor
                    ref={editorRef}
                    value={body}
                    onChange={setBody}
                    placeholder="Type your message... Use {{firstName}}, {{lastName}}, {{product}}, {{amount}}, {{lender}}, {{loanNumber}}, {{propertyAddress}}, {{estimatedClosingDate}}"
                    toolbarExtra={<DynamicVariableInsertButton editor={editorRef.current} />}
                  />
                  <HStack mt={2}>
                    <Button size="xs" variant="ghost" leftIcon={<FiEye />} onClick={() => setShowPreview(true)}>
                      Preview
                    </Button>
                  </HStack>
                  <EmailPreviewModal
                    isOpen={showPreview}
                    onClose={() => setShowPreview(false)}
                    body={body}
                    subject={subject}
                    fromName="Future Operations Hub"
                  />
                </>
              ) : (
                <Textarea
                  ref={smsBodyRef}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Type your message... Any contact, deal, or sender variable works, e.g. {{firstName}}, {{deal.loanAmount}}, {{loanOfficerName}}"
                  rows={6}
                  fontFamily="monospace"
                  fontSize="sm"
                />
              )}
              <Text fontSize="xs" color="gray.500" mt={1}>
                {body.length} characters
                {templateType === 'sms' && body.length > 160 && (
                  <Text as="span" color="orange.500">
                    {' '}(will send as {Math.ceil(body.length / 160)} messages)
                  </Text>
                )}
              </Text>
            </FormControl>

            {templateType === 'sms' && (
              <FormControl>
                <FormLabel fontSize="sm">Image (optional)</FormLabel>
                <SmsMediaPicker media={media} onChange={setMedia} />
                {media && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Sends as an MMS wherever this template is used.
                  </Text>
                )}
              </FormControl>
            )}

            <FormControl display="flex" alignItems="center">
              <FormLabel fontSize="sm" mb={0}>
                Active
              </FormLabel>
              <Switch
                isChecked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                colorScheme="green"
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={isSaving}
            loadingText="Saving..."
          >
            {template ? 'Update Template' : 'Create Template'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

